/**
 * LoggingGuard - Utility for conditionally enabling/disabling logging
 * 
 * Provides a wrapper function that checks LoggingConfigService before
 * executing logging code. This allows runtime control of logging without
 * modifying the logger implementations.
 * 
 * @example
 * // In a logger wrapper
 * const logKeyEvent = guard('input.keyEvent', (key: string) => {
 *   logger.debug('input.keyEvent', `Key: ${key}`);
 * });
 * 
 * // Usage - won't log if input.keyEvent is disabled
 * logKeyEvent('a');
 */

import { LoggingConfigService } from '../../services/LoggingConfigService';

/**
 * Create a guarded logging function
 * Returns a function that only executes if the component is enabled
 * 
 * @param component - The component identifier (e.g., 'input.keyEvent')
 * @param fn - The logging function to execute if enabled
 * @returns A wrapped function that checks config before executing
 */
export function guard<T extends (...args: any[]) => any>(
  component: string,
  fn: T
): T {
  return ((...args: Parameters<T>) => {
    if (!LoggingConfigService.isEnabled(component)) {
      return;
    }
    return fn(...args);
  }) as T;
}

/**
 * Create a namespace of guarded logging functions
 * Useful for creating logger objects with multiple methods
 * 
 * @param baseComponent - The base component prefix (e.g., 'input')
 * @param methods - Object of method names to functions
 * @returns Object with guarded methods
 * 
 * @example
 * const editLogger = guardNamespace('input.edit', {
 *   insert: (text: string) => logger.debug('input.edit.insert', text),
 *   delete: (count: number) => logger.debug('input.edit.delete', count),
 * });
 * 
 * editLogger.insert('hello'); // Only logs if input.edit or input.edit.insert is enabled
 */
export function guardNamespace<T extends Record<string, (...args: any[]) => any>>(
  baseComponent: string,
  methods: T
): T {
  const guarded = {} as T;
  
  for (const [name, fn] of Object.entries(methods)) {
    const component = `${baseComponent}.${name}`;
    (guarded as any)[name] = guard(component, fn);
  }
  
  return guarded;
}

/**
 * Check if logging is enabled for a component (direct access)
 * Useful for conditional blocks that do expensive computation before logging
 * 
 * @param component - The component identifier
 * @returns true if logging is enabled
 * 
 * @example
 * if (isLoggingEnabled('wasm.edit')) {
 *   const expensiveData = computeExpensiveData();
 *   WasmLogger.edit.complete(expensiveData);
 * }
 */
export function isLoggingEnabled(component: string): boolean {
  return LoggingConfigService.isEnabled(component);
}

/**
 * Higher-order function decorator for class methods
 * 
 * @param component - The component identifier
 * @returns Method decorator
 * 
 * @example
 * class MyLogger {
 *   @guardMethod('input.keyEvent')
 *   logKeyEvent(key: string) {
 *     // ...
 *   }
 * }
 */
export function guardMethod(component: string) {
  return function (
    _target: any,
    _propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;
    
    descriptor.value = function (...args: any[]) {
      if (!LoggingConfigService.isEnabled(component)) {
        return;
      }
      return originalMethod.apply(this, args);
    };
    
    return descriptor;
  };
}

/**
 * Conditional logging utility
 * Only executes the callback if the component is enabled
 * Useful for complex logging scenarios
 * 
 * @param component - The component identifier
 * @param callback - Function to execute if enabled
 * 
 * @example
 * logIf('wasm.edit.diag', () => {
 *   const state = captureComplexState();
 *   WasmLogger.edit.diag(state);
 * });
 */
export function logIf(component: string, callback: () => void): void {
  if (LoggingConfigService.isEnabled(component)) {
    callback();
  }
}
